import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Clients.css';

type MaterialCategory =
  | 'Cement'
  | 'Steel'
  | 'Bricks'
  | 'Sand'
  | 'Tiles'
  | 'Paints'
  | 'Wood'
  | 'Electrical'
  | 'Plumbing'
  | 'Hardware'
  | 'Interior Furniture'
  | 'Other';

interface MaterialVendor {
  id: number;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  category: MaterialCategory;
  address?: string;
  notes?: string;
  projects?: Array<{ id: number; name: string; status: string }>;
  projectsCount?: number;
}

const MATERIAL_CATEGORIES: MaterialCategory[] = [
  'Cement',
  'Steel',
  'Bricks',
  'Sand',
  'Tiles',
  'Paints',
  'Wood',
  'Electrical',
  'Plumbing',
  'Hardware',
  'Interior Furniture',
  'Other'
];

const MaterialVendors: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<MaterialVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<MaterialVendor | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    category: 'Cement' as MaterialCategory,
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/material-vendors');

      // Map vendors with projects parsed from their notes field
      const vendorsWithProjects = response.data.map((vendor: any) => {
        // Parse project names from notes field (one per line)
        const projectNames: string[] = [];
        if (vendor.notes && vendor.notes.trim()) {
          const names = vendor.notes.split('\n').filter((name: string) => name.trim());
          projectNames.push(...names.map((name: string) => name.trim()));
        }

        const projects = projectNames.map((name: string, index: number) => ({
          id: index,
          name: name,
          status: 'Active'
        }));

        return {
          ...vendor,
          projects: projects,
          projectsCount: projects.length
        };
      });

      setVendors(vendorsWithProjects);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load material vendors');
      console.error('Error fetching material vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingVendor(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      category: 'Cement',
      address: '',
      notes: ''
    });
    setShowAddModal(true);
  };

  const handleOpenEditModal = (vendor: MaterialVendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      phone: vendor.phone,
      email: vendor.email || '',
      company: vendor.company || '',
      category: vendor.category,
      address: vendor.address || '',
      notes: vendor.notes || ''
    });
    setShowAddModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.category) {
      alert('Please fill in name, phone number, and category');
      return;
    }

    if (formData.phone.length !== 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      if (editingVendor) {
        // Update existing vendor
        const response = await api.put(`/material-vendors/${editingVendor.id}`, formData);
        setVendors(vendors.map(v => v.id === editingVendor.id ? response.data : v));
      } else {
        // Add new vendor
        const response = await api.post('/material-vendors', formData);
        setVendors([...vendors, response.data]);
      }
      setShowAddModal(false);
    } catch (err: any) {
      alert(err.response?.data?.message || `Failed to ${editingVendor ? 'update' : 'add'} material vendor`);
      console.error('Error saving material vendor:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this material vendor?')) {
      return;
    }

    try {
      await api.delete(`/material-vendors/${id}`);
      setVendors(vendors.filter(v => v.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete material vendor');
      console.error('Error deleting material vendor:', err);
    }
  };

  const filteredVendors = selectedCategory === 'All'
    ? vendors
    : vendors.filter(v => v.category === selectedCategory);

  return (
    <div className="clients-container">
      <header className="clients-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            ← Back
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
          <h1>Material Vendors</h1>
        </div>
        <button className="btn-add-client" onClick={handleOpenAddModal}>+ Add Material Vendor</button>
      </header>

      {/* Category Filter */}
      <div style={{ padding: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setSelectedCategory('All')}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border: 'none',
            background: selectedCategory === 'All' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e0e0e0',
            color: selectedCategory === 'All' ? 'white' : '#333',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          All ({vendors.length})
        </button>
        {MATERIAL_CATEGORIES.map(category => {
          const count = vendors.filter(v => v.category === category).length;
          return (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                background: selectedCategory === category ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e0e0e0',
                color: selectedCategory === category ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {category} ({count})
            </button>
          );
        })}
      </div>

      <div className="clients-content">
        {loading && <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#666' }}>Loading material vendors...</div>}

        {error && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#f44336', fontSize: '16px', marginBottom: '20px' }}>{error}</p>
            <button
              onClick={fetchVendors}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filteredVendors.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>
              {selectedCategory === 'All' ? 'No material vendors yet' : `No vendors in ${selectedCategory} category`}
            </p>
            <p style={{ fontSize: '14px' }}>Click "+ Add Material Vendor" to get started</p>
          </div>
        )}

        {!loading && !error && filteredVendors.length > 0 && (
          <div className="clients-grid">
            {filteredVendors.map((vendor) => (
              <div
                key={vendor.id}
                className="client-card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/material-vendor/${vendor.id}`)}
              >
                <div className="client-avatar" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                  {vendor.name.charAt(0).toUpperCase()}
                </div>
                <div className="client-info">
                  <h3>{vendor.name}</h3>
                  {vendor.company && <p style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>{vendor.company}</p>}
                  <p className="client-phone">📱 {vendor.phone}</p>
                  {vendor.email && <p className="client-email">✉️ {vendor.email}</p>}
                  <div style={{ marginTop: '8px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {vendor.category}
                    </span>
                  </div>
                  {vendor.address && (
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                      📍 {vendor.address}
                    </p>
                  )}
                  {vendor.projects && vendor.projects.length > 0 && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>Projects:</p>
                      {vendor.projects.map((project) => (
                        <div key={project.id} style={{ fontSize: '13px', color: '#333', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span>📋</span>
                          <span>{project.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditModal(vendor);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(vendor.id);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{editingVendor ? 'Edit Material Vendor' : 'Add Material Vendor'}</h2>
              <button className="btn-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  placeholder="Enter vendor name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  placeholder="Enter 10-digit phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  maxLength={10}
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as MaterialCategory })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  {MATERIAL_CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Email (Optional)</label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Company (Optional)</label>
                <input
                  type="text"
                  placeholder="Enter company name"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Address (Optional)</label>
                <textarea
                  placeholder="Enter address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div className="form-group">
                <label>Project Names (Optional)</label>
                <textarea
                  placeholder="Enter project names (one per line)"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleSubmit}>
                {editingVendor ? 'Update Vendor' : 'Add Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialVendors;
